// myumaps.ca/mobile/changelog — what changed in the app, and who asked for it.
//
// One column, newest first, read on a phone like the /mobile page it hangs off.
// Deliberately plain: this is a page somebody opens after tapping "what's new",
// reads for twenty seconds and leaves.
//
// ⚠️ THE QUOTES ARE THE POINT, not decoration. A student who reports a broken
// route hears nothing back — there is no account, no email, nowhere to reply —
// so the only way they ever learn it was read is finding their own complaint on
// a page saying it was fixed. That is why an entry can carry the feedback that
// prompted it, showing when it was sent, from which app, and what it said.
//
// Which also means the dates matter more than they look: "reported 3 September,
// fixed 10 September" is the whole story, and it only works because an attached
// quote keeps the time the STUDENT sent it rather than the time it was attached
// (see api/_changelog.js).
//
// Nothing here is authenticated and nothing is submitted from it.

import { useEffect, useState } from 'react';
import UMapsWordmark from './components/UMapsWordmark.jsx';

const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';

// The same four the store keeps, in the words a student reads. "known" is
// deliberately not styled as a failure — an app that admits to a known issue is
// being straight with somebody who has already hit it.
const KIND = {
  added: { label: 'New', bg: '#e6f4ea', fg: '#137333', bd: '#a8dab5' },
  fixed: { label: 'Fixed', bg: '#e5edfd', fg: '#1a56c4', bd: '#c9dbfb' },
  improved: { label: 'Better', bg: '#f3e8fd', fg: '#7627bb', bd: '#e0c9fb' },
  known: { label: 'Known issue', bg: '#fef7e0', fg: '#a35a00', bd: '#f6d08a' },
};

/** "3 September 2026" — spelled out, because this page is read, not scanned. */
function fmtDay(ts) {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleDateString(undefined, {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch { return ''; }
}

/** "3 Sep" — for the quotes, where the date sits inside a line of small type. */
function fmtShort(ts) {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  } catch { return ''; }
}

/**
 * The body, as paragraphs.
 *
 * Blank-line-separated plain text and nothing else — no markdown, no HTML. The
 * operator is typing into a textarea on a dashboard and this text is rendered
 * on a public page, so the narrowest thing that can express "a few paragraphs"
 * is the right one. React escapes it either way; this is about not inviting a
 * syntax nobody documented.
 */
function Body({ text }) {
  const paras = String(text || '').split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  return paras.map((p, i) => <p key={i} style={S.body}>{p}</p>);
}

export default function ChangelogPage() {
  const [posts, setPosts] = useState(null); // null = still loading
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/changelog`);
        const j = await r.json().catch(() => ({}));
        if (!alive) return;
        if (!r.ok || !j.ok) { setErr('Could not load the changelog.'); setPosts([]); return; }
        setPosts(Array.isArray(j.posts) ? j.posts : []);
      } catch {
        if (alive) { setErr('Could not load the changelog.'); setPosts([]); }
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div style={S.page}>
      <style>{CSS}</style>
      <div style={S.wrap}>
        <header style={S.header}>
          <UMapsWordmark size={26} href="/" style={{ fontSize: 22, color: '#1a56c4', letterSpacing: '-0.02em' }} />
          <span style={S.badge}>App</span>
        </header>

        <h1 style={S.h1}>What&rsquo;s new</h1>
        <p style={S.sub}>
          Updates to the UMaps app, newest first. A lot of these came from
          students telling us something was wrong &mdash; where they did,
          you&rsquo;ll see what they said.
        </p>

        {posts === null && <div style={S.quiet}>Loading&hellip;</div>}
        {!!err && <div style={S.quiet}>{err}</div>}

        {posts !== null && !err && posts.length === 0 && (
          <div style={S.quiet}>Nothing here yet &mdash; check back after the next update.</div>
        )}

        {(posts || []).map((p) => {
          const k = KIND[p.kind] || KIND.improved;
          return (
            <article key={p.id} style={S.post}>
              <div style={S.postTop}>
                {/* The full `border`, not a `borderColor` over a shorthand in
                    S.kind — React warns about mixing the two on one element,
                    and the styling it produces on a rerender is genuinely
                    undefined rather than merely untidy. */}
                <span style={{ ...S.kind, background: k.bg, color: k.fg, border: `1px solid ${k.bd}` }}>
                  {k.label}
                </span>
                {!!p.version && <span style={S.version}>v{p.version}</span>}
                <span style={S.day}>{fmtDay(p.ts)}</span>
              </div>

              <h2 style={S.h2}>{p.title}</h2>
              <Body text={p.body} />

              {/* The feedback this entry answers. Headed, because without a line
                  saying what it is a quoted sentence from a stranger reads as a
                  testimonial — which is the opposite of what it is. */}
              {!!p.items?.length && (
                <div style={S.said}>
                  <div style={S.saidHead}>
                    {p.items.length === 1 ? 'Someone told us:' : 'Students told us:'}
                  </div>
                  {p.items.map((it, i) => (
                    <blockquote key={i} style={S.quote}>
                      <span style={S.quoteText}>{it.text}</span>
                      <span style={S.quoteMeta}>
                        {[fmtShort(it.ts), it.device].filter(Boolean).join(' · ')}
                      </span>
                    </blockquote>
                  ))}
                </div>
              )}
            </article>
          );
        })}

        <footer style={S.footer}>
          <a style={S.footLink} href="/mobile">&larr; Get the app</a>
          <a style={S.footLink} href="/umanitoba">Open the web map</a>
          <a style={S.footLink} href="/mobile-privacy-policy">Privacy policy</a>
        </footer>
      </div>
    </div>
  );
}

const S = {
  // Same ground as /mobile, so arriving here from that page is one site and not
  // two — this is reached from a link on it and links back.
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #eef4ff 0%, #f7f9fc 46%, #ffffff 100%)',
    fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
    color: '#16223a',
    padding: '0 0 calc(28px + env(safe-area-inset-bottom, 0px))',
    boxSizing: 'border-box',
  },
  wrap: { width: 'min(620px, 100%)', margin: '0 auto', padding: '18px 18px 0', boxSizing: 'border-box' },
  header: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 },
  badge: {
    fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase',
    background: '#e5edfd', border: '1px solid #c9dbfb', color: '#1a56c4',
    borderRadius: 999, padding: '3px 9px',
  },
  h1: {
    fontSize: 'clamp(29px, 8.5vw, 40px)', lineHeight: 1.1, fontWeight: 900,
    letterSpacing: '-0.03em', margin: '0 0 12px', color: '#0f1e38',
  },
  sub: { fontSize: 15.5, lineHeight: 1.5, color: '#54617a', margin: '0 0 26px' },
  quiet: { fontSize: 14.5, color: '#54617a', padding: '18px 0' },
  post: {
    background: '#fff', border: '1px solid #e3e8f0', borderRadius: 14,
    padding: '16px 16px 18px', marginBottom: 14,
    boxShadow: '0 2px 10px rgba(20,40,80,0.05)',
  },
  postTop: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 },
  kind: {
    fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase',
    borderRadius: 999, padding: '3px 9px',
  },
  version: {
    fontSize: 12, fontWeight: 700, color: '#54617a',
    background: '#f1f4f9', borderRadius: 999, padding: '3px 8px',
  },
  day: { fontSize: 12.5, color: '#7b8699', marginLeft: 'auto' },
  h2: { fontSize: 19, fontWeight: 800, letterSpacing: '-0.01em', margin: '0 0 8px', color: '#0f1e38' },
  body: { fontSize: 15, lineHeight: 1.55, color: '#3c4761', margin: '0 0 10px' },
  said: { marginTop: 14, paddingTop: 12, borderTop: '1px solid #eef1f6' },
  saidHead: {
    fontSize: 11.5, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
    color: '#8a94a6', marginBottom: 8,
  },
  // A left rule rather than a card: a quote inside an entry is part of it, and
  // boxing it would make two things to read where there is one.
  quote: {
    margin: '0 0 8px', padding: '2px 0 2px 12px', borderLeft: '3px solid #d9e2f2',
    display: 'flex', flexDirection: 'column', gap: 3,
  },
  quoteText: { fontSize: 14.5, lineHeight: 1.45, color: '#3c4761', fontStyle: 'italic' },
  quoteMeta: { fontSize: 12, color: '#8a94a6' },
  footer: {
    display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'center',
    marginTop: 28, paddingTop: 18, borderTop: '1px solid #e3e8f0',
  },
  footLink: { color: '#5f6b82', fontSize: 13, fontWeight: 600, textDecoration: 'none' },
};

const CSS = `
  body { margin: 0; }
  /* The quoted text is the one thing here that is somebody else's writing and
     can be any length or shape, including one unbroken 200-character token. */
  blockquote { overflow-wrap: anywhere; }
`;
